import { useState } from 'react';
import { FileUpload, FileUploadDropZoneProps } from './file-upload-base';
import { Alert } from '../../base/alerts/alerts';
import { Label } from '../../base/input/label';

export type FileListProps = { file: File; id: string };

type ImageUploadProps = FileUploadDropZoneProps & {
  files: FileListProps[];
  setFiles: (value: FileListProps[]) => void;
  label?: React.ReactNode;
  description?: React.ReactNode;
  labelClassName?: string;
  isRequired?: boolean;
};

export function ImageUpload(props: ImageUploadProps) {
  const {
    files,
    setFiles,
    allowsMultiple,
    label,
    description,
    labelClassName,
    isRequired,
    accept = 'image/*',
    ...otherProps
  } = props;

  const [error, setError] = useState<string>();
  const handleDropFiles = (files: FileList) => {
    setError(undefined);
    const newFiles = Array.from(files).map((file) => ({
      id: `${Date.now()}-${file.name}`,
      file
    }));

    if (!allowsMultiple && newFiles[0]) {
      setFiles([newFiles[0]]);
      return;
    }

    setFiles([...props.files, ...newFiles]);
  };

  const handleDeleteFile = (id: string) => {
    setError(undefined);
    setFiles(files.filter((file) => file.id !== id));
  };

  const handleDropUnacceptedFiles = (files: FileList) => {
    setError(
      `Unsupported file type: ${Array.from(files)
        .map((file) => `${file.name} (${file.type || 'unknown type'})`)
        .join(', ')}`
    );
  };

  const handleSizeLimitExceed = (files: FileList) => {
    setError(
      `The following files exceed the size limit: ${Array.from(files)
        .map(
          (file) => `${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`
        )
        .join(', ')}`
    );
  };

  return (
    <div className="group flex h-max w-full flex-col gap-1.5">
      {label && (
        <Label className={labelClassName} isRequired={isRequired}>
          {label}
        </Label>
      )}
      {description && (
        <div className="text-xs text-tertiary -mt-1">{description}</div>
      )}
      <FileUpload.Root>
        <FileUpload.DropZone
          hint="Select images to upload"
          allowsMultiple={allowsMultiple}
          {...otherProps}
          accept={accept}
          onDropFiles={handleDropFiles}
          onDropUnacceptedFiles={handleDropUnacceptedFiles}
          onSizeLimitExceed={handleSizeLimitExceed}
        />
        {files.length > 0 && (
          <FileUpload.List>
            {files.map((file) => (
              <FileUpload.ListItemProgressBar
                key={file.id}
                name={file.file.name}
                size={file.file.size}
                type={file.file.type}
                onDelete={() => handleDeleteFile(file.id)}
              />
            ))}
          </FileUpload.List>
        )}
        {error && <Alert color="error" title={error} />}
      </FileUpload.Root>
    </div>
  );
}
